import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useLanguage } from '../../context/LanguageContext';
import { i18n } from '../../i18n/i18n';
import { KidButton, KidCard } from '../kid/KidUI';
import AntMascot from '../AntMascot';
import { BILLING_CONFIG } from '../../constants/billing';
import { formatDeletionDate } from '../../utils/accountDeletion';
import type { FamilyBilling } from '../../shared/types';
import { getBillingPortalUrl } from '../../services/apiService';
import { showToast } from '../kid/Toast';

/**
 * Settings → Subscription (admins), and what an admin sees when the family's access has ended
 * (payments groundwork, 2026-10-12, behind FEATURES.payments). Checkout stays disabled until the
 * Lemon Squeezy links are in constants/billing.ts; "Manage" opens Lemon Squeezy's customer portal.
 */
const SubscriptionPage: React.FC = () => {
  const { current_family } = useAppContext() as any;
  const { current_language } = useLanguage();
  const navigate = useNavigate();
  const [openingPortal, setOpeningPortal] = useState(false);
  const billing: FamilyBilling | null = current_family?.billing ?? null;
  const date = (iso: string | null) => (iso ? formatDeletionDate(iso, current_language) : '');
  const t = (key: string, vars: Record<string, string | number> = {}) =>
    Object.entries(vars).reduce((s, [k, v]) => s.split(`{{${k}}}`).join(String(v)), i18n.t(`billing.${key}`));

  const status = (() => {
    if (!billing) return null;
    switch (billing.state) {
      case 'trial': return { title: t('trial_title', { days: billing.trialDaysLeft ?? 0 }), text: t('trial_text', { date: date(billing.trialEndsAt) }) };
      case 'expired': return { title: t('expired_title'), text: t('expired_text') };
      case 'active': return {
        title: t(billing.plan === 'yearly' ? 'active_title_yearly' : 'active_title_monthly'),
        text: billing.renewsAt ? t('active_text', { date: date(billing.renewsAt) }) : '',
      };
      case 'grace': return { title: t('grace_title'), text: t('grace_text', { date: date(billing.until) }) };
      case 'cancelling': return { title: t('cancelling_title'), text: t('cancelling_text', { date: date(billing.until) }) };
      case 'exempt': return { title: t('exempt_title'), text: t('exempt_text') };
      default: return null;
    }
  })();

  const checkout = (plan: 'monthly' | 'yearly') => {
    const url = BILLING_CONFIG.checkoutUrls[plan];
    if (!url || !current_family?.id) return;
    const u = new URL(url);
    u.searchParams.set('checkout[custom][family_id]', current_family.id);
    window.location.href = u.toString();
  };
  // Lemon Squeezy's portal: change plan or card, receipts, cancel (2026-10-13).
  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      window.location.href = await getBillingPortalUrl();
    } catch {
      showToast({ message: t('manage_failed'), tone: 'error' });
      setOpeningPortal(false);
    }
  };
  const checkoutReady = !!(BILLING_CONFIG.checkoutUrls.monthly || BILLING_CONFIG.checkoutUrls.yearly);
  const showPlans = billing?.state !== 'active' && billing?.state !== 'exempt';

  const planCard = (plan: 'monthly' | 'yearly') => (
    <KidCard key={plan} tone={plan === 'yearly' ? 'mint' : 'card'} className="flex flex-col gap-3">
      <h3 className="font-kid-heading font-extrabold text-2xl">{t(`plan_${plan}`)}</h3>
      <p className="text-lg font-bold">
        {BILLING_CONFIG.prices[plan] ? t(`price_${plan}`, { price: BILLING_CONFIG.prices[plan] as string }) : t('price_soon')}
      </p>
      <ul className="list-disc pl-5 text-base space-y-1">
        <li>{t('includes_family')}</li>
        <li>{t('includes_tasks')}</li>
        <li>{t('includes_cancel')}</li>
      </ul>
      <KidButton size="md" variant={plan === 'yearly' ? 'primary' : 'secondary'} disabled={!BILLING_CONFIG.checkoutUrls[plan]} onClick={() => checkout(plan)}>
        {t(`choose_${plan}`)}
      </KidButton>
    </KidCard>
  );

  return (
    <div className="w-full max-w-3xl mx-auto px-4 md:px-8 py-4 md:py-7 space-y-6 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      <div className="flex items-center gap-3">
        {billing?.hasAccess !== false && (
          <button type="button" onClick={() => navigate('/settings')} aria-label={i18n.t('settings.back')}
            className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center shrink-0">
            <span aria-hidden>←</span>
          </button>
        )}
        <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl">{t('title')}</h1>
      </div>

      {status && (
        <KidCard tone={billing?.hasAccess === false || billing?.state === 'grace' ? 'peach' : 'mint'} className="flex items-start gap-4">
          <AntMascot className="w-16 h-16 shrink-0" />
          <div className="space-y-1">
            <h2 className="font-kid-heading font-extrabold text-2xl">{status.title}</h2>
            {status.text && <p className="text-base">{status.text}</p>}
          </div>
        </KidCard>
      )}

      {showPlans && (
        <section className="space-y-3">
          <h2 className="font-kid-heading font-extrabold text-2xl">{t('plans_title')}</h2>
          <p className="text-base">{t('plans_intro')}</p>
          <div className="grid gap-4 md:grid-cols-2">{planCard('monthly')}{planCard('yearly')}</div>
          {!checkoutReady && <p className="text-base font-bold">{t('coming_soon')}</p>}
        </section>
      )}

      {(billing?.state === 'active' || billing?.state === 'grace' || billing?.state === 'cancelling') && (
        <KidCard className="space-y-2">
          <h2 className="font-kid-heading font-extrabold text-xl">{t('manage_title')}</h2>
          <p className="text-base">{t('manage_text')}</p>
          <KidButton size="md" variant="secondary" disabled={openingPortal} onClick={openPortal}>
            {openingPortal ? '…' : t('manage_button')}
          </KidButton>
        </KidCard>
      )}
    </div>
  );
};

export default SubscriptionPage;
